import { partnerBackgrounds, partnerLogos } from './assets';
import environment, { ICountry } from '../utils/environment';
import { IColors } from '../hooks/useTheme';
import { setCurrentPartnerId } from '../utils/request';
import { updatePageBranding } from '../utils/updatePageBranding';
import layout from './layout';

interface ITheme {
  headerBackgroundColor?: string;
  headerTextColor?: string;
  headerKryLiviLogoTint?: keyof typeof IColors;
  colors?: Partial<Record<keyof typeof IColors, string>>;
}

interface IPartner {
  id: string;
  basePath: string;
  logo?: string;
  smartBanner?: boolean;
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  hideKryLiviLogoSection?: boolean;
  theme?: ITheme;
  contextualLoginView?: boolean;
  favicon?: string;
  pageTitle?: string;
  logoutRedirectUrl?: { prod: string; test: string };
  // When true, the app menu (tab bar / sidebar) is hidden throughout the
  // booking deep-link flow, so it runs full-screen without the possibility to navigate away
  hideAppMenuDuringBooking?: boolean;
  // The partner-specific booking entry deep-link key (e.g. '/book/vitality').
  // This is used to hide the app menu on that view, whilst the booking
  // flow resolves. This needs to match the partner-specific booking deep-link key from View Layer
  bookingDeepLinkKey?: string;
}

const partners: Record<ICountry, Array<IPartner>> = {
  SE: [
    {
      id: 'light-test',
      basePath: '/partner/light-test',
      logo: partnerLogos.TESTPARTNER_LIGHT,

      theme: {
        headerBackgroundColor: '#644AFF',
        headerTextColor: 'rgba(255, 255, 255, 0.6)',
        headerKryLiviLogoTint: 'LOCAL_WHITE',
        colors: {
          PRIMARY: '#644AFF',
          PRIMARY_PRESS: '#3420A9',
          SECONDARY: '#F0EDFF',
          SECONDARY_PRESS: '#C1B7FF',
        },
      },
    },
    {
      id: 'test',
      basePath: '/partner/test',
      logo: partnerLogos.TESTPARTNER,
    },
    {
      id: 'vgr',
      basePath: '/partner/vgr',
      logo: partnerLogos.VGR,
    },
  ],

  NO: [
    {
      id: 'test',
      basePath: '/partner/test',
      logo: partnerLogos.TESTPARTNER,
    },
  ],

  GB: [
    {
      id: 'lime',
      basePath: '/partner/embeddable/lime',
      hideKryLiviLogoSection: true,
      smartBanner: false,
      theme: {
        colors: { EMBEDDABLE_PARTNER_BG: 'LOCAL_WHITE' },
      },
    },
    {
      id: 'numan',
      basePath: '/partner/embeddable/numan',
      hideKryLiviLogoSection: true,
      smartBanner: false,
      theme: {
        colors: { EMBEDDABLE_PARTNER_BG: 'LOCAL_WHITE' },
      },
    },
    {
      id: 'vitality',
      hideKryLiviLogoSection: true,
      basePath: '/partner/vitality',
      backgroundImage: partnerBackgrounds.VITALITY,
      backgroundSize: 'contain',
      backgroundPosition: 'bottom',
      smartBanner: false,
      theme: {
        colors: { LOCAL_APP_BG: '#ECEFF4' },
      },
      contextualLoginView: true,
      favicon: '/favicon_vitality.png',
      pageTitle: 'Vitality – See a GP by video',
      logoutRedirectUrl: {
        prod: 'https://members.vitality.co.uk',
        test: 'https://test.members.vitality.co.uk',
      },
      hideAppMenuDuringBooking: true,
      bookingDeepLinkKey: '/book/vitality',
    },
  ],

  FR: [
    {
      id: 'healthpoint',
      basePath: '/partner/telemedica',
      logo: partnerLogos.TELEMEDICA,
    },
    {
      id: 'mon-bilan-cardio',
      basePath: '/partner/mon-bilan-cardio',
      logo: partnerLogos.MALAKOFF,
    },
    {
      id: 'mon-bilan-cancer',
      basePath: '/partner/mon-bilan-cancer',
      logo: partnerLogos.MBK,
    },
    {
      id: 'april',
      basePath: '/partner/april',
      logo: partnerLogos.APRIL,
    },
    {
      id: 'benefiz',
      basePath: '/partner/benefiz',
      logo: partnerLogos.BENEFIZ,
      backgroundImage: partnerBackgrounds.BENEFIZ,
    },
    {
      id: 'jetransporte',
      basePath: '/partner/jetransporte',
      logo: partnerLogos.JETRANSPORTE,
      backgroundImage: partnerBackgrounds.JETRANSPORTE,
    },
    {
      id: 'elsan',
      basePath: '/partner/elsan',
      logo: partnerLogos.ELSAN,
      backgroundImage: partnerBackgrounds.ELSAN,
      hideKryLiviLogoSection: true,

      theme: {
        headerBackgroundColor: '#FFF2DE',
        colors: {
          PRIMARY: '#2D7799',
          PRIMARY_PRESS: '#002845',
          SECONDARY: '#C7E1ED',
          SECONDARY_PRESS: '#78B2CC',
        },
      },
    },
    {
      id: 'sidecare',
      basePath: '/partner/sidecare',
      logo: partnerLogos.SIDECARE,
      backgroundImage: partnerBackgrounds.SIDECARE,
      theme: {
        colors: {
          PRIMARY: '#6D092A',
          PRIMARY_PRESS: '#870B34',
          SECONDARY: '#FFF8EB',
          SECONDARY_PRESS: '#FFE8BE',
        },
      },
    },
    {
      id: 'aglae',
      basePath: '/partner/aglae',
      logo: partnerLogos.AGLAE,
    },
    {
      id: 'prevoir',
      basePath: '/partner/prevoir',
      logo: partnerLogos.PREVOIR,
    },
    {
      id: 'mindday',
      basePath: '/partner/mindday',
      logo: partnerLogos.MINDDAY,
    },
    {
      id: 'kor',
      basePath: '/partner/kor',
      logo: partnerLogos.KOR,
    },
    {
      id: 'lucis',
      basePath: '/partner/lucis',
      logo: partnerLogos.LUCIS,
    },
  ],
};

// TODO: Check how does partners currently work.
const currentPartner = partners[environment.COUNTRY].find((partner) =>
  window.location.pathname.startsWith(partner.basePath)
);

// Set the global custom partner header.
if (currentPartner) {
  setCurrentPartnerId(currentPartner.id);

  // Update page branding (favicon and title) if partner has custom branding
  if (currentPartner.favicon || currentPartner.pageTitle) {
    updatePageBranding({
      favicon: currentPartner.favicon,
      title: currentPartner.pageTitle,
    });
  }

  if (
    currentPartner.backgroundImage &&
    !document.head.querySelector('[data-partner-bg-preload]')
  ) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('data-partner-bg-preload', '');
    link.media = `(min-width: ${layout.BREAKPOINT_MEDIUM}px)`;
    link.href = currentPartner.backgroundImage;
    document.head.appendChild(link);
  }
}

const isLime = (): boolean => currentPartner?.id.toLowerCase() === 'lime';
const isNuman = (): boolean => currentPartner?.id.toLowerCase() === 'numan';
const isEmbeddable = (): boolean => isLime() || isNuman();
const isVitality = (): boolean =>
  currentPartner?.id.toLowerCase() === 'vitality';

const getLogoutRedirectUrl = (): string | undefined => {
  if (!currentPartner?.logoutRedirectUrl) return undefined;
  return environment.IS_PROD
    ? currentPartner.logoutRedirectUrl.prod
    : currentPartner.logoutRedirectUrl.test;
};

export { isLime, isNuman, isEmbeddable, isVitality, getLogoutRedirectUrl };
export default currentPartner;
